import { t } from "i18next";
import { useCallback, useState } from "react";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { backendService } from "../../services/backend.service";
import { isNurseActiveInDepartment } from "../../utils/formatters";
import type { TeamNurse } from "../../utils/models";

// A nurse belonging to multiple departments can only have its status changed from their home
// facility. Mirrors the single-employee menu guard in TeamTable so bulk actions stay consistent.
const canChangeStatusInDepartment = (nurse: TeamNurse, departmentId: string): boolean =>
	nurse.departments.length <= 1 || nurse.homeDepartmentId === departmentId;

/**
 * How many status mutations are allowed in flight at once.
 *
 * Selection is no longer a mode you enter from a menu, so one click on the header checkbox now arms
 * the whole filtered roster — 544 people on the facility this was measured against, and ~840 on the
 * largest. Each request deletes that nurse's future shifts, drops their recurring assignments and
 * cancels their pending requests server-side, so firing the batch unthrottled points a few hundred
 * concurrent write transactions at one database from one click. Eight keeps the batch comfortably
 * faster than doing it by hand without making a routine action look like an outage.
 */
const BULK_STATUS_CONCURRENCY = 8;

/**
 * `Promise.allSettled` over `items`, but never more than `limit` in flight.
 *
 * Results come back positionally, exactly as `allSettled` would return them, so callers keep
 * counting fulfilled/rejected the same way. Workers pull from a shared cursor rather than running
 * fixed-size batches, so one slow request cannot idle the rest of its batch.
 */
async function settleWithConcurrency<T, R>(items: T[], limit: number, run: (item: T) => Promise<R>): Promise<PromiseSettledResult<R>[]> {
	const results = new Array<PromiseSettledResult<R>>(items.length);
	let cursor = 0;
	const worker = async () => {
		while (cursor < items.length) {
			const index = cursor++;
			// `allSettled` semantics: a rejection is recorded, never thrown, so one failure cannot
			// abandon the rest of the selection half-processed.
			results[index] = await run(items[index])
				.then((value): PromiseSettledResult<R> => ({ status: "fulfilled", value }))
				.catch((reason): PromiseSettledResult<R> => ({ status: "rejected", reason }));
		}
	};
	await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
	return results;
}

/**
 * Why each selected nurse was left out. The two skip reasons are unrelated — "you can't change this
 * person from here" vs "this person is already in the target state" — and reporting them under one
 * label let the bar claim home-facility skips that never happened.
 */
type BulkPartition = {
	eligible: TeamNurse[];
	skippedNotHomeFacility: number;
	skippedAlreadyInState: number;
};

type BulkSummaryKeys = {
	doneKey: string;
	alreadyInStateKey: string;
};

/**
 * Splits the selection into who the batch will act on and why the rest were skipped. The facility
 * check runs first so a nurse who fails both reasons is counted once, under the reason that
 * actually blocks the action.
 */
const partitionSelection = (nurses: TeamNurse[], departmentId: string, isAlreadyInTargetState: (nurse: TeamNurse) => boolean): BulkPartition => {
	const partition: BulkPartition = { eligible: [], skippedNotHomeFacility: 0, skippedAlreadyInState: 0 };
	for (const nurse of nurses) {
		if (!canChangeStatusInDepartment(nurse, departmentId)) {
			partition.skippedNotHomeFacility += 1;
		} else if (isAlreadyInTargetState(nurse)) {
			partition.skippedAlreadyInState += 1;
		} else {
			partition.eligible.push(nurse);
		}
	}
	return partition;
};

const buildSummary = (succeeded: number, failed: number, partition: BulkPartition, keys: BulkSummaryKeys): string => {
	const parts = [t(keys.doneKey, { count: succeeded })];
	if (partition.skippedNotHomeFacility > 0) parts.push(t("team.bulk.skipped_home_facility", { count: partition.skippedNotHomeFacility }));
	if (partition.skippedAlreadyInState > 0) parts.push(t(keys.alreadyInStateKey, { count: partition.skippedAlreadyInState }));
	if (failed > 0) parts.push(t("team.bulk.failed", { count: failed }));
	return parts.join(" · ");
};

/** The first rejection, so a partial failure is diagnosable instead of just a count. */
const firstRejectionReason = (results: PromiseSettledResult<unknown>[]): unknown =>
	results.find((result): result is PromiseRejectedResult => result.status === "rejected")?.reason;

const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/**
 * Orchestrates mass activate/deactivate. To keep bulk actions fast, it calls the backend endpoints
 * directly (via `settleWithConcurrency`) rather than the single-employee mutation hooks, which each fire
 * their own requests-refresh and error snackbar per call. Data is refreshed once at the end.
 * Nurses that can only be changed from their home facility, or already in the target state, are
 * skipped and reported separately.
 */
export const useBulkNurseStatus = () => {
	const { chosenDepartment, refreshNursesData, refreshDepartmentData } = useHospitalData();
	const { refreshRequests } = useRequestsContext();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const [isBulkActivating, setIsBulkActivating] = useState(false);
	const [isBulkDeactivating, setIsBulkDeactivating] = useState(false);

	const departmentId = chosenDepartment.department_id;

	const activateNurses = useCallback(
		async (nurses: TeamNurse[]) => {
			const partition = partitionSelection(nurses, departmentId, (n) => isNurseActiveInDepartment(n, departmentId));
			if (partition.eligible.length === 0) {
				showSnackbar(t("team.bulk.none_eligible.activate"), "info");
				return;
			}

			setIsBulkActivating(true);
			track("team_bulk_activate_start", { department_id: departmentId, count: partition.eligible.length });
			try {
				const results = await settleWithConcurrency(partition.eligible, BULK_STATUS_CONCURRENCY, (n) => backendService.activateNurse(departmentId, n.id));
				const succeeded = results.filter((r) => r.status === "fulfilled").length;
				const failed = results.length - succeeded;
				// Reactivation restores each nurse's recurring schedule server-side, so the grid reloads too (DAT-712).
				await Promise.all([refreshNursesData(departmentId), refreshDepartmentData(departmentId, false)]);
				// Refresh is part of the operation: only emit a terminal success/partial event after the UI's
				// dependent data is current, so a refresh rejection cannot coexist with success telemetry.
				if (failed > 0) {
					trackError("team_bulk_activate_partial_failure", {
						department_id: departmentId,
						failed_count: failed,
						error_message: errorMessage(firstRejectionReason(results)),
					});
				} else {
					track("team_bulk_activate_success", { department_id: departmentId, count: succeeded });
				}
				showSnackbar(
					buildSummary(succeeded, failed, partition, { doneKey: "team.bulk.activate.done", alreadyInStateKey: "team.bulk.skipped_already_active" }),
					failed > 0 ? "warning" : "success",
				);
			} catch (error) {
				// settleWithConcurrency never rejects; this guards the post-batch refresh so a failed refresh is
				// surfaced instead of becoming an unhandled rejection in the caller.
				trackError("team_bulk_activate_error", { department_id: departmentId, error_message: errorMessage(error) });
				showSnackbar(t("team.bulk.error"), "error");
			} finally {
				setIsBulkActivating(false);
			}
		},
		[departmentId, refreshNursesData, refreshDepartmentData, showSnackbar, track, trackError],
	);

	const deactivateNurses = useCallback(
		async (nurses: TeamNurse[], postShifts: boolean) => {
			const partition = partitionSelection(nurses, departmentId, (n) => !isNurseActiveInDepartment(n, departmentId));
			if (partition.eligible.length === 0) {
				showSnackbar(t("team.bulk.none_eligible.deactivate"), "info");
				return;
			}

			setIsBulkDeactivating(true);
			track("team_bulk_deactivate_start", { department_id: departmentId, count: partition.eligible.length });
			try {
				const results = await settleWithConcurrency(partition.eligible, BULK_STATUS_CONCURRENCY, (n) =>
					backendService.scheduleNurseDeactivation(departmentId, n.id, "", postShifts),
				);
				const succeeded = results.filter((r) => r.status === "fulfilled").length;
				const failed = results.length - succeeded;
				// Deactivation cancels pending/swap requests and deletes future shifts server-side; refresh all
				// three stores once for the whole batch so the schedule grid drops the removed nurses (DAT-712).
				await Promise.all([refreshNursesData(departmentId), refreshRequests(), refreshDepartmentData(departmentId, false)]);
				if (failed > 0) {
					trackError("team_bulk_deactivate_partial_failure", {
						department_id: departmentId,
						failed_count: failed,
						error_message: errorMessage(firstRejectionReason(results)),
					});
				} else {
					track("team_bulk_deactivate_success", { department_id: departmentId, count: succeeded });
				}
				showSnackbar(
					buildSummary(succeeded, failed, partition, { doneKey: "team.bulk.deactivate.done", alreadyInStateKey: "team.bulk.skipped_already_inactive" }),
					failed > 0 ? "warning" : "success",
				);
			} catch (error) {
				// settleWithConcurrency never rejects; this guards the post-batch refresh so a failed refresh is
				// surfaced instead of becoming an unhandled rejection in the caller.
				trackError("team_bulk_deactivate_error", { department_id: departmentId, error_message: errorMessage(error) });
				showSnackbar(t("team.bulk.error"), "error");
			} finally {
				setIsBulkDeactivating(false);
			}
		},
		[departmentId, refreshNursesData, refreshRequests, refreshDepartmentData, showSnackbar, track, trackError],
	);

	return { activateNurses, deactivateNurses, isBulkActivating, isBulkDeactivating };
};
